import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";

// ─────────────────────────────────────────────────────────────────────────────
// UserManagement.js  (per-project membership model)
//
// Users no longer have one global role. Instead:
//   - globalRole: "administrator" | null   (admins see everything)
//   - memberships: { [projectId]: "engineer" | "site_engineer" | "client" }
//
// Creating a user now creates the Firebase Auth account AND the /users doc in
// one go via the `createUser` Cloud Function — no more Console UID copying.
// A password-reset link is returned for you to hand to the new user.
//
// Requires Cloud Functions: createUser, setMemberships, deleteUser (see
// functions/index.js). Custom claims are synced server-side by syncUserClaims.
// ─────────────────────────────────────────────────────────────────────────────

const PROJECT_ROLES = [
  { value: "engineer",      label: "Engineer / QS", color: "#2563eb",
    desc: "Full commercial + site write access on this project." },
  { value: "site_engineer", label: "Site Engineer", color: "#059669",
    desc: "Capture diaries, RFIs, NCRs, instructions. Read elsewhere." },
  { value: "client",        label: "Client",        color: "#d97706",
    desc: "Read-only: financials, certificates, programme." },
];

const roleColor = (v) =>
  (PROJECT_ROLES.find((r) => r.value === v) || { color: "#6b7280" }).color;
const roleLabel = (v) =>
  (PROJECT_ROLES.find((r) => r.value === v) || { label: v }).label;

const EMPTY_FORM = { displayName: "", email: "", isAdmin: false, memberships: {} };

const functions = getFunctions();
const fnCreateUser     = httpsCallable(functions, "createUser");
const fnSetMemberships = httpsCallable(functions, "setMemberships");
const fnDeleteUser     = httpsCallable(functions, "deleteUser");

export default function UserManagement({ currentUser }) {
  const [users,    setUsers]    = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients,  setClients]  = useState([]);
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [editUid,  setEditUid]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState("");

  // Live listeners
  useEffect(() => {
    const u1 = onSnapshot(collection(db, "users"), (s) =>
      setUsers(s.docs.map((d) => ({ id: d.id, ...d.data() }))));
    const u2 = onSnapshot(collection(db, "projects"), (s) =>
      setProjects(s.docs.map((d) => ({ id: d.id, ...d.data() }))));
    const u3 = onSnapshot(collection(db, "clients"), (s) =>
      setClients(s.docs.map((d) => ({ id: d.id, ...d.data() }))));
    return () => { u1(); u2(); u3(); };
  }, []);

  // Group projects under their client for the assignment UI
  const projectsByClient = useMemo(() => {
    const byId = Object.fromEntries(clients.map((c) => [c.id, c.name]));
    const groups = {};
    projects.forEach((p) => {
      const key = p.clientId || "_none";
      (groups[key] ||= { name: byId[p.clientId] || "Unassigned", projects: [] })
        .projects.push(p);
    });
    return groups;
  }, [projects, clients]);

  const patch = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const setProjectRole = (projectId, role) =>
    setForm((f) => {
      const next = { ...f.memberships };
      if (!role) delete next[projectId];
      else next[projectId] = role;
      return { ...f, memberships: next };
    });

  const handleSave = async () => {
    setError(""); setSuccess("");
    if (!form.displayName.trim()) { setError("Display name is required."); return; }
    if (!form.email.trim())       { setError("Email is required."); return; }

    setSaving(true);
    try {
      if (editUid) {
        await fnSetMemberships({
          uid: editUid,
          isAdmin: form.isAdmin,
          memberships: form.memberships,
        });
        setSuccess(`${form.displayName} updated. They may need to sign out/in to refresh access.`);
      } else {
        const res = await fnCreateUser({
          displayName: form.displayName.trim(),
          email: form.email.trim(),
          isAdmin: form.isAdmin,
          memberships: form.memberships,
        });
        const link = res?.data?.resetLink;
        setSuccess(
          link
            ? `User created. Send them this password-reset link to set their own password:\n${link}`
            : `User created.`
        );
      }
      setForm(EMPTY_FORM);
      setEditUid(null);
    } catch (err) {
      setError(err.message || "Operation failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (user) => {
    setForm({
      displayName: user.displayName || "",
      email: user.email || "",
      isAdmin: user.globalRole === "administrator",
      memberships: user.memberships || {},
    });
    setEditUid(user.uid || user.id);
    setError(""); setSuccess("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete ${user.displayName || user.email}? This removes their login AND access permanently.`)) return;
    setError(""); setSuccess("");
    try {
      await fnDeleteUser({ uid: user.uid || user.id });
      setSuccess(`${user.displayName || user.email} deleted.`);
    } catch (err) {
      setError(err.message || "Delete failed.");
    }
  };

  const handleCancel = () => { setForm(EMPTY_FORM); setEditUid(null); setError(""); setSuccess(""); };

  // styles
  const card  = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, padding: "20px 24px", boxShadow: "0 4px 16px rgba(0,0,0,0.06)" };
  const label = { display: "block", fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" };
  const input = { width: "100%", border: "1px solid #e2e8f0", borderRadius: 8, padding: "9px 12px", fontSize: 14, color: "#0f172a", outline: "none", boxSizing: "border-box", background: "#f8fafc" };

  return (
    <section style={{ padding: "28px 32px", maxWidth: 1000, margin: "0 auto", fontFamily: "Inter, sans-serif" }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 24, fontWeight: 800, color: "#0f172a" }}>User Management</h2>
      <p style={{ margin: "0 0 28px", color: "#64748b", fontSize: 14 }}>
        Create logins and assign per-project roles. Administrators see every project.
        Everyone else only sees projects they're a member of.
      </p>

      {/* Add / Edit */}
      <div style={{ ...card, marginBottom: 24 }}>
        <h3 style={{ margin: "0 0 18px", fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
          {editUid ? "Edit User Access" : "Create User"}
        </h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div>
            <label style={label}>Display Name</label>
            <input style={input} placeholder="e.g. Jane Smith"
              value={form.displayName} onChange={(e) => patch("displayName", e.target.value)} />
          </div>
          <div>
            <label style={label}>Email</label>
            <input style={input} type="email" placeholder="jane@organisation.com"
              value={form.email} onChange={(e) => patch("email", e.target.value)}
              readOnly={!!editUid} />
          </div>
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18, cursor: "pointer" }}>
          <input type="checkbox" checked={form.isAdmin}
            onChange={(e) => patch("isAdmin", e.target.checked)} />
          <span style={{ fontSize: 13, fontWeight: 700, color: "#7c3aed" }}>
            Administrator (full access to all projects)
          </span>
        </label>

        {!form.isAdmin && (
          <div style={{ marginBottom: 18 }}>
            <label style={label}>Project Access</label>
            {projects.length === 0 ? (
              <p style={{ fontSize: 13, color: "#94a3b8" }}>No projects yet. Create projects first.</p>
            ) : (
              Object.entries(projectsByClient).map(([cid, group]) => (
                <div key={cid} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", margin: "8px 0 6px" }}>
                    {group.name}
                  </div>
                  {group.projects.map((p) => (
                    <div key={p.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", border: "1px solid #f1f5f9", borderRadius: 8, marginBottom: 6, background: form.memberships[p.id] ? roleColor(form.memberships[p.id]) + "0D" : "#fff" }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{p.name || p.id}</span>
                      <select
                        style={{ ...input, width: 180, padding: "6px 10px", fontSize: 13, cursor: "pointer" }}
                        value={form.memberships[p.id] || ""}
                        onChange={(e) => setProjectRole(p.id, e.target.value)}
                      >
                        <option value="">No access</option>
                        {PROJECT_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        )}

        {error   && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", color: "#dc2626", fontSize: 13, marginBottom: 12, whiteSpace: "pre-wrap" }}>{error}</div>}
        {success && <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 14px", color: "#16a34a", fontSize: 13, marginBottom: 12, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{success}</div>}

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}>
            {saving ? "Saving…" : (editUid ? "Update Access" : "Create User")}
          </button>
          {editUid && (
            <button onClick={handleCancel}
              style={{ background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Register */}
      <div style={card}>
        <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
          Active Users ({users.length})
        </h3>
        {users.length === 0 ? (
          <p style={{ color: "#94a3b8", fontSize: 14, textAlign: "center", padding: "24px 0" }}>No users yet.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #f1f5f9" }}>
                  {["Name", "Email", "Access", "Actions"].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const isAdmin = user.globalRole === "administrator";
                  const mem = user.memberships || {};
                  return (
                    <tr key={user.id} style={{ borderBottom: "1px solid #f8fafc" }}>
                      <td style={{ padding: "10px 12px", fontWeight: 600, color: "#0f172a" }}>{user.displayName || "—"}</td>
                      <td style={{ padding: "10px 12px", color: "#475569" }}>{user.email || "—"}</td>
                      <td style={{ padding: "10px 12px" }}>
                        {isAdmin ? (
                          <span style={{ padding: "3px 10px", borderRadius: 20, background: "#7c3aed18", color: "#7c3aed", fontSize: 11, fontWeight: 700, border: "1px solid #7c3aed44" }}>
                            Administrator · all projects
                          </span>
                        ) : Object.keys(mem).length === 0 ? (
                          <span style={{ fontSize: 12, color: "#94a3b8" }}>No project access</span>
                        ) : (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {Object.entries(mem).map(([pid, role]) => {
                              const proj = projects.find((p) => p.id === pid);
                              const c = roleColor(role);
                              return (
                                <span key={pid} title={`${roleLabel(role)} on ${proj?.name || pid}`}
                                  style={{ padding: "2px 8px", borderRadius: 14, background: c + "14", color: c, fontSize: 11, fontWeight: 700, border: `1px solid ${c}33` }}>
                                  {(proj?.name || pid).slice(0, 18)} · {roleLabel(role)}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={() => handleEdit(user)}
                            style={{ background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Edit</button>
                          {(user.uid || user.id) !== currentUser?.uid && (
                            <button onClick={() => handleDelete(user)}
                              style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Delete</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
